import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ShieldAlert, Upload, FileText, Image as ImageIcon, Eye, Trash2, Loader2 } from "lucide-react";

const DOC_TYPES = ["Driver's License", "Passport", "Social Security Card", "Birth Certificate", "Credit Report", "Other"];

export default function ClientDocuments({ clientId }) {
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState("Driver's License");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(() => api.get(`/clients/${clientId}/documents`).then((r) => setDocs(r.data)).catch(() => {}), [clientId]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) return toast.error("Choose a file first");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", docType);
      fd.append("file", file);
      await api.post(`/clients/${clientId}/documents`, fd);
      toast.success(`${docType} uploaded`);
      setFile(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); } finally { setUploading(false); }
  };

  const view = async (doc) => {
    setViewing(doc.id);
    try {
      const res = await api.get(`/client-documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Could not open document"); } finally { setViewing(null); }
  };

  const remove = async (id) => { await api.delete(`/client-documents/${id}`); toast.success("Document removed"); load(); };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-200/90 flex gap-3" data-testid="documents-disclaimer">
        <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" />
        <p>Store sensitive identity documents securely — driver's license, passport, Social Security card, and birth certificate. Files are encrypted at rest and only accessible from your admin session.</p>
      </div>

      <Card className="p-6 bg-card/60 border-border" data-testid="upload-document-card">
        <h3 className="font-display text-xl font-semibold mb-4">Upload Identity Document</h3>
        <div className="grid sm:grid-cols-[1fr_1.5fr_auto] gap-3 items-end">
          <div className="space-y-2">
            <Label>Document Type</Label>
            <select value={docType} onChange={(e) => setDocType(e.target.value)} className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm" data-testid="document-type-select">
              {DOC_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label>File (image or PDF)</Label>
            <label className="flex items-center gap-2 bg-secondary border border-border border-dashed rounded-md px-3 py-2 text-sm cursor-pointer hover:border-primary/50 transition-colors">
              <Upload className="h-4 w-4 text-muted-foreground" />
              <span className="truncate text-muted-foreground">{file ? file.name : "Choose file…"}</span>
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="document-file-input" />
            </label>
          </div>
          <Button onClick={upload} disabled={uploading} className="rounded-lg" data-testid="upload-document-btn">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" /> Upload</>}
          </Button>
        </div>
      </Card>

      <div>
        <h3 className="font-display text-xl font-semibold mb-3">Stored Documents</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {docs.map((d) => {
            const isPdf = (d.content_type || "").includes("pdf");
            const Icon = isPdf ? FileText : ImageIcon;
            return (
              <Card key={d.id} className="p-4 bg-card/60 border-border flex items-center justify-between" data-testid={`document-${d.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-lg bg-primary/15 flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-primary" /></div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{d.doc_type}</div>
                    <div className="text-xs text-muted-foreground truncate">{d.original_filename}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => view(d)} disabled={viewing === d.id} data-testid={`view-document-${d.id}`}>
                    {viewing === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(d.id)} data-testid={`delete-document-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </Card>
            );
          })}
          {!docs.length && <p className="text-sm text-muted-foreground py-4">No documents uploaded yet.</p>}
        </div>
      </div>
    </div>
  );
}
